import { useState, useRef, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { saveProcurementTableNote } from '../../../shared/api/procurementNotes.api'
import { toast } from '../../../shared/ui/toastStore'

/** Граница сервера для заметки (procurement-table-notes.routes.ts). */
const NOTE_MAX_LENGTH = 5000

export function ProcNoteCell({
  itemId,
  initialNote,
  onHeightChange,
}: {
  itemId: string
  initialNote: string
  onHeightChange: () => void
}) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(initialNote)
  const taRef = useRef<HTMLTextAreaElement>(null)
  // Enter сохраняет и переводит фокус на строку ниже, а потеря фокуса вызывает
  // сохранение ещё раз с тем же значением: без этой отметки уходили два PUT, а
  // при ошибке показывались бы два одинаковых тоста.
  const savingRef = useRef<string | null>(null)

  // Авто-ресайз textarea
  const autoResize = useCallback(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
    onHeightChange()
  }, [onHeightChange])

  // Синхронизируем при обновлении данных с сервера
  useEffect(() => {
    setValue(initialNote)
    requestAnimationFrame(autoResize)
  }, [initialNote, autoResize])

  const save = async (currentValue: string) => {
    if (currentValue === initialNote) return
    if (savingRef.current === currentValue) return
    savingRef.current = currentValue
    try {
      await saveProcurementTableNote({ requestItemId: itemId, note: currentValue })
      void queryClient.invalidateQueries({ queryKey: ['procurement-table-notes'] })
    } catch (err: unknown) {
      // Раньше отказ глотался молча: закупщик считал заметку сохранённой, а после
      // обновления страницы текст пропадал. Текст в поле не откатываем, чтобы
      // его можно было поправить и сохранить снова.
      const apiMessage = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      toast.error(apiMessage ?? 'Не удалось сохранить заметку')
    } finally {
      savingRef.current = null
    }
  }

  // Переход на следующую строку (к ячейке ниже)
  const moveToNextRow = () => {
    if (!taRef.current) return
    const all = Array.from(
      document.querySelectorAll<HTMLTextAreaElement>('textarea[data-proc-note]'),
    )
    const idx = all.indexOf(taRef.current)
    if (idx >= 0 && idx < all.length - 1) all[idx + 1]?.focus()
  }

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      // Cmd/Ctrl+Enter, Alt/Option+Enter, Shift+Enter → новая строка
      // (Excel-paradigm: Option+Enter на Mac, Alt+Enter на Windows).
      // Просто Enter — сохранить и перейти на строку ниже.
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
        e.preventDefault()
        const el = e.currentTarget
        const start = el.selectionStart ?? value.length
        const end = el.selectionEnd ?? value.length
        const next = value.slice(0, start) + '\n' + value.slice(end)
        setValue(next)
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = start + 1
          autoResize()
        })
      } else {
        e.preventDefault()
        void save(value)
        moveToNextRow()
      }
    }
  }

  return (
    <textarea
      ref={taRef}
      data-proc-note
      value={value}
      maxLength={NOTE_MAX_LENGTH}
      onChange={(e) => {
        setValue(e.target.value)
        autoResize()
      }}
      onBlur={(e) => void save(e.target.value)}
      onKeyDown={handleKey}
      rows={1}
      style={{ overflow: 'hidden' }}
      className="w-full text-xs resize-none bg-transparent border border-transparent hover:border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 leading-relaxed"
      placeholder="Заметка..."
    />
  )
}
